import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TwitterIcon,
  GithubIcon,
  InstagramIcon,
  LinkedInIcon,
} from "./SocialIcons";
import { socialLinks, startupsContent } from "../../data/commonData";

const ANIMATION_DURATION = 0.2;
const STAGGER_DELAY = 0.075;

const listItems = [
  {
    text: "MERN Stack | Full-Stack Development | Cloud Enthusiast",
  },
  {
    text: "Computer Science Engineering (AI & ML) @ PCCOE, Pune",
  },
  {
    text: "Worked with a few startups",
    dropdown: true,
    content: startupsContent,
  },
  {
    text: "Interested in: Cloud, DevOps practices, GenAI, RAG",
  },
  {
    text: "Open for: Technical discussions, Collaborations, Startup opportunities",
    link: "https://wa.me/7378360027?text=I want to work on a project with you",
    linkText: "connect",
  },
];

const DropdownContent = ({
  isOpen,
  items,
  onMouseEnter,
  onMouseLeave,
  className = "",
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0, y: -15, scale: 0.95 }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            duration: ANIMATION_DURATION,
            ease: [0.4, 0, 0.2, 1],
          },
        }}
        exit={{
          opacity: 0,
          y: -15,
          scale: 0.95,
          transition: {
            duration: ANIMATION_DURATION * 0.75,
            ease: [0.4, 0, 1, 1],
          },
        }}
        className={`absolute mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 z-20 p-4 backdrop-blur-sm ${className}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {items.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: ANIMATION_DURATION * 1.25,
              delay: index * STAGGER_DELAY,
              ease: [0.25, 0.1, 0.25, 1],
              scale: {
                type: "spring",
                damping: 12,
                stiffness: 200,
                mass: 0.2,
              },
            }}
            whileHover={{
              scale: 1.02,
              transition: { duration: 0.2 },
            }}
            className="px-4 py-2 cursor-pointer rounded-md"
          >
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {item.title || item.name}
            </p>
            {item.details && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {item.details}
              </p>
            )}
            {item.role && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {item.role} • {item.period}
              </p>
            )}
          </motion.div>
        ))}
      </motion.div>
    )}
  </AnimatePresence>
);

const HeroSection = () => {
  const [isStartupsOpen, setIsStartupsOpen] = useState(false);
  const startupsDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const refs = [startupsDropdownRef];
      const states = [setIsStartupsOpen];

      refs.forEach((ref, index) => {
        if (ref.current && !ref.current.contains(event.target)) {
          states[index](false);
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full mx-auto px-4 sm:px-6 lg:px-8" id="about">
      <div className="max-w-3xl mx-auto flex flex-col md:flex-row justify-between items-start md:space-x-14 w-full mb-10">
        <div className="mt-4 order-2 md:order-1">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Omkar Khoche
          </h1>

          <ul className="space-y-3 list-none mt-4">
            {listItems.map((item, index) => (
              <li key={index} className="flex items-start">
                <div className="flex relative">
                  {item.dropdown ? (
                    <div
                      ref={startupsDropdownRef}
                      onMouseLeave={() => setIsStartupsOpen(false)}
                    >
                      <motion.span
                        className="dark:text-gray-400 cursor-pointer hover:text-indigo-500 transition-colors"
                        onMouseEnter={() => setIsStartupsOpen(true)}
                        whileHover={{ scale: 1.05 }}
                      >
                        {item.text}
                      </motion.span>
                      <DropdownContent
                        isOpen={isStartupsOpen}
                        items={item.content}
                        onMouseEnter={() => setIsStartupsOpen(true)}
                        onMouseLeave={() => setIsStartupsOpen(false)}
                        className="absolute left-0 w-72"
                      />
                    </div>
                  ) : (
                    <span>
                      <span className="dark:text-gray-400">{item.text}</span>
                      {item.link && (
                        <a
                          href={item.link}
                          className="text-indigo-500 hover:underline"
                        >
                          {" "}
                          {item.linkText}
                        </a>
                      )}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4">
            <div className="text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
              Follow me on Twitter and LinkedIn.
            </div>
          </div>

          {/* Social media icons */}
          <div className="flex mt-6 space-x-4">
            {socialLinks.map((link, index) => {
              const Icon = { TwitterIcon, GithubIcon, InstagramIcon, LinkedInIcon }[
                link.icon
              ];
              return (
                <motion.a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 hover:-translate-y-1 p-2 rounded-full"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={link.label}
                >
                  <Icon className="w-5 h-5 text-gray-700 dark:text-gray-300 transition-colors" />
                </motion.a>
              );
            })}
          </div>
        </div>

        <div className="relative order-1 md:order-2">
          <div className="relative">
            <img
              src="https://avatars.githubusercontent.com/u/141771647?v=4"
              alt="Profile"
              className="relative w-24 h-24 rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-md z-10 mt-3 mr-5 md:mb-0 mb-4 transition-all duration-300 hover:shadow-lg"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/10 rounded-lg blur-md -z-10 transform scale-105"></div>
          </div>

          <div className="absolute inset-0 z-0">
            <div className="absolute left-0 -right-12 top-0 h-px bg-slate-900/[0.1] dark:bg-zinc-300/[0.1] [mask-image:linear-gradient(to_right,transparent,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute -top-8 bottom-0 left-12 w-px bg-slate-900/[0.1] dark:bg-zinc-300/[0.1] [mask-image:linear-gradient(to_top,transparent,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute left-0 -right-12 bottom-14 h-px bg-slate-900/[0.1] dark:bg-zinc-300/[0.1] [mask-image:linear-gradient(to_right,transparent,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute right-0 -top-2 -bottom-8 w-px bg-slate-900/[0.1] dark:bg-zinc-300/[0.1] [mask-image:linear-gradient(to_top,transparent,white_4rem,white_calc(100%-4rem),transparent)]"></div>
            <div className="absolute bottom-full right-10 -mb-px flex h-8 items-end overflow-hidden">
              <div className="flex -mb-px h-[2px] w-40 -scale-x-100">
                <div className="w-full flex-none blur-sm [background-image:linear-gradient(90deg,rgba(56,189,248,0)_0%,#0EA5E9_32.29%,rgba(236,72,153,0.3)_67.19%,rgba(236,72,153,0)_100%)]"></div>
                <div className="-ml-[100%] w-full flex-none blur-[1px] [background-image:linear-gradient(90deg,rgba(56,189,248,0)_0%,#0EA5E9_32.29%,rgba(236,72,153,0.3)_67.19%,rgba(236,72,153,0)_100%)]"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
           